import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import "./CreateGroupForm.css";
import axios from "axios";

const CreateGroupForm = ({ onClose, group = null }) => {
  const [formData, setFormData] = useState({
    nom: group?.nom || "",
    ville: group?.ville || "",
    code_postal: group?.code_postal || "",
    numero_voie: group?.numero_voie || "",
    extension: group?.extension || "",
    type_voie: group?.type_voie || "",
    nom_voie: group?.nom_voie || "",
    telephone: group?.telephone || "",
    espace_note: group?.espace_note || "",
  });

  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5002/api";

  useEffect(() => {
    document.body.classList.add('modal-open');

    return () => {
      document.body.classList.remove('modal-open');
    };
  }, []);

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    if (!formData.nom || formData.nom.trim() === "") {
      errors.nom = "Le nom du groupe est requis";
      isValid = false;
    }

    if (!formData.ville || formData.ville.trim() === "") {
      errors.ville = "La ville est requise";
      isValid = false;
    }

    if (!formData.numero_voie || formData.numero_voie.trim() === "") {
      errors.numero_voie = "Le numéro de voie est requis";
      isValid = false;
    }

    if (!formData.type_voie || formData.type_voie.trim() === "") {
      errors.type_voie = "Le type de voie est requis";
      isValid = false;
    }

    if (!formData.nom_voie || formData.nom_voie.trim() === "") {
      errors.nom_voie = "Le nom de la voie est requis";
      isValid = false;
    }

    if (!formData.code_postal || formData.code_postal.trim() === "") {
      errors.code_postal = "Le code postal est requis";
      isValid = false;
    } else if (formData.code_postal.length !== 5) {
      errors.code_postal = "Le code postal doit contenir 5 chiffres";
      isValid = false;
    }

    if (!formData.telephone || formData.telephone.trim() === "") {
      errors.telephone = "Le téléphone est requis";
      isValid = false;
    } else if (formData.telephone.length !== 10) {
      errors.telephone = "Le téléphone doit contenir 10 chiffres";
      isValid = false;
    }

    setFieldErrors(errors);

    if (!isValid) {
      setError("Veuillez corriger les erreurs dans le formulaire");
    }

    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setError(null);

    if (fieldErrors[name]) {
      setFieldErrors({
        ...fieldErrors,
        [name]: null
      });
    }

    if (name === "telephone" && value) {
      if (value !== '' && !/^\d+$/.test(value)) {
        setFieldErrors({
          ...fieldErrors,
          [name]: "Le téléphone ne doit contenir que des chiffres"
        });
        return;
      }
      
      const onlyNumbers = value.replace(/[^\d]/g, "");
      if (onlyNumbers.length <= 10) {
        setFormData((prev) => ({
          ...prev,
          [name]: onlyNumbers,
        }));
        
        if (onlyNumbers.length > 0 && onlyNumbers.length < 10) {
          setFieldErrors({
            ...fieldErrors,
            [name]: "Le téléphone doit contenir 10 chiffres"
          });
        } else {
          setFieldErrors({
            ...fieldErrors,
            [name]: null
          });
        }
      }
      return;
    }

    if (name === "code_postal" && value) {
      if (value !== '' && !/^\d+$/.test(value)) {
        setFieldErrors({
          ...fieldErrors,
          [name]: "Le code postal ne doit contenir que des chiffres"
        });
        return;
      }
      
      const onlyNumbers = value.replace(/[^\d]/g, "");
      if (onlyNumbers.length <= 5) {
        setFormData((prev) => ({
          ...prev,
          [name]: onlyNumbers,
        }));
        
        if (onlyNumbers.length > 0 && onlyNumbers.length < 5) {
          setFieldErrors({
            ...fieldErrors,
            [name]: "Le code postal doit contenir 5 chiffres"
          });
        } else {
          setFieldErrors({
            ...fieldErrors,
            [name]: null
          });
        }
      }
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/groups`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data) {
        alert("Groupe créé avec succès");
        onClose();
      }
    } catch (error) {
      console.error("Erreur lors de la création du groupe:", error);
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        if (errorData.field) {
          setFieldErrors({
            ...fieldErrors,
            [errorData.field]: errorData.message || "Champ invalide"
          });
          setError(errorData.message || "Veuillez corriger le formulaire");
        } else if (errorData.errorType === 'DUPLICATE_GROUP') {
          setFieldErrors({
            ...fieldErrors,
            nom: "Ce nom de groupe est déjà utilisé"
          });
          setError("Ce nom de groupe est déjà utilisé");
        } else if (errorData.errorType === 'DUPLICATE_TELEPHONE') {
          setFieldErrors({
            ...fieldErrors,
            telephone: "Ce numéro de téléphone est déjà utilisé par un autre groupe"
          });
          setError("Ce numéro de téléphone est déjà utilisé");
        } else {
          setError(errorData.message || "Erreur lors de la création du groupe");
        }
      } else {
        setError("Erreur lors de la création du groupe");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target.className === 'modal-overlay') {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content">
        <div className="modal-header">
          <h2>Créer un nouveau groupe</h2>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="modal-body">
          <div className="form-section">
            <div className="form-group">
              <label className="required-field">Nom du groupe</label>
              <input
                type="text"
                name="nom"
                value={formData.nom}
                onChange={handleChange}
                required
                placeholder="Ex: Groupe VéhiDoc"
                autoFocus
                className={fieldErrors.nom ? "input-error" : ""}
              />
              {fieldErrors.nom && <div className="field-error">{fieldErrors.nom}</div>}
            </div>
          </div>

          <div className="form-section">
            <div className="form-group-row">
              <div className="form-group">
                <label className="required-field">Ville</label>
                <input
                  type="text"
                  name="ville"
                  value={formData.ville}
                  onChange={handleChange}
                  required
                  placeholder="Ex: Rennes"
                  className={fieldErrors.ville ? "input-error" : ""}
                />
                {fieldErrors.ville && <div className="field-error">{fieldErrors.ville}</div>}
              </div>

              <div className="form-group">
                <label className="required-field">Code Postal</label>
                <input
                  type="text"
                  name="code_postal"
                  value={formData.code_postal}
                  onChange={handleChange}
                  placeholder="Ex: 35000"
                  required
                  maxLength={5}
                  className={fieldErrors.code_postal ? "input-error" : ""}
                />
                {fieldErrors.code_postal && <div className="field-error">{fieldErrors.code_postal}</div>}
              </div>
            </div>

            <div className="form-group-row">
              <div className="form-group">
                <label className="required-field">Numéro de Voie</label>
                <input
                  type="text"
                  name="numero_voie"
                  value={formData.numero_voie}
                  onChange={handleChange}
                  required
                  placeholder="Ex: 123"
                  className={fieldErrors.numero_voie ? "input-error" : ""}
                />
                {fieldErrors.numero_voie && <div className="field-error">{fieldErrors.numero_voie}</div>}
              </div>

              <div className="form-group">
                <label>Extension</label>
                <input
                  type="text"
                  name="extension"
                  value={formData.extension}
                  onChange={handleChange}
                  placeholder="Ex: Bis"
                  className={fieldErrors.extension ? "input-error" : ""}
                />
                {fieldErrors.extension && <div className="field-error">{fieldErrors.extension}</div>}
              </div>
            </div>

            <div className="form-group-row">
              <div className="form-group">
                <label className="required-field">Type de Voie</label>
                <input
                  type="text"
                  name="type_voie"
                  value={formData.type_voie}
                  onChange={handleChange}
                  required
                  placeholder="Ex: Rue"
                  className={fieldErrors.type_voie ? "input-error" : ""}
                />
                {fieldErrors.type_voie && <div className="field-error">{fieldErrors.type_voie}</div>}
              </div>

              <div className="form-group">
                <label className="required-field">Nom de la Voie</label>
                <input
                  type="text"
                  name="nom_voie"
                  value={formData.nom_voie}
                  onChange={handleChange}
                  required
                  placeholder="Ex: de la République"
                  className={fieldErrors.nom_voie ? "input-error" : ""}
                />
                {fieldErrors.nom_voie && <div className="field-error">{fieldErrors.nom_voie}</div>}
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="form-group">
              <label className="required-field">Téléphone</label>
              <input
                type="text"
                name="telephone"
                value={formData.telephone}
                onChange={handleChange}
                required
                placeholder="Ex: 0123456789"
                maxLength={10}
                className={fieldErrors.telephone ? "input-error" : ""}
              />
              {fieldErrors.telephone && <div className="field-error">{fieldErrors.telephone}</div>}
            </div>

            <div className="form-group">
              <label>Espace Note</label>
              <textarea
                name="espace_note"
                value={formData.espace_note}
                onChange={handleChange}
                placeholder="Notes supplémentaires"
                className={fieldErrors.espace_note ? "input-error" : ""}
              />
              {fieldErrors.espace_note && <div className="field-error">{fieldErrors.espace_note}</div>}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="button secondary" onClick={onClose}>
            Annuler
          </button>
          <button
            type="button"
            className="button primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Création en cours..." : "Créer le groupe"}
          </button>
        </div>
      </div>
    </div>
  );
};

CreateGroupForm.propTypes = {
  onClose: PropTypes.func.isRequired,
  group: PropTypes.object,
};

export default CreateGroupForm;
